import { useContext, useEffect, useState } from 'react';
import { Link, useLocation, withRouter } from 'react-router-dom';

import Box from '@mui/material/Box';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import docImg from '../../../../assets/icons/document.png';
import BadgeStatus from '../../../../common/BadgeStatus';
import Searchbar from '../../../../components/Searchbar';
import ToastUtil from '../../../../components/ToastNotification/ToastUtil';
import { config } from '../../../../config';

import { GET, newFetch, POST } from '../../../../helpers/fetch';
import { getSelectedSiteIds } from '../../../../helpers/general';
import AppContext from '../../../../store/App/contexts';

// Suppress ResizeObserver warnings
const originalError = console.error;
console.error = (...args) => {
  if (
    args[0] &&
    ((typeof args[0] === 'string' && args[0].includes('ResizeObserver')) ||
      args[0].message?.includes('ResizeObserver'))
  ) {
    return;
  }
  originalError.apply(console, args);
};

const { SERVER_URL } = config;

const LeavesApproval = ({ history }) => {
  const { selectedClientID, selectedSiteID } = useContext(AppContext);
  const clientID = parseInt(selectedClientID, 10);
  const siteID = parseInt(selectedSiteID, 10);
  const siteIDs = getSelectedSiteIds(clientID, siteID);
  const siteIDsKey = siteIDs.join(',');
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [leaves, setLeaves] = useState([]);
  const [claimTypes, setClaimTypes] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [sortModel, setSortModel] = useState([
    { field: 'updatedAt', sort: 'desc' },
  ]);
  const [totalRows, setTotalRows] = useState(0);
  const [data, setData] = useState([]);
  const type = localStorage.getItem('type');
  const [clients, setClients] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [searchText, setSearchText] = useState('');

  // NEW: state for modal
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [remarksText, setRemarksText] = useState('');

  const columns = [
    {
      field: 'leave_id',
      headerName: 'Request ID',
      flex: 0.6,
      sortable: true,
      renderCell: (params) => {
        return (
          <Link
            className="text-primary"
            to={{
              pathname: `/leave/approval/edit/${params.row.leave_id}/approver/${params.row.approver_level}`,
              state: { from: location.pathname },
            }}
            state={{ from: location.pathname }}
            style={{ fontWeight: 700, fontSize: 14 }}
          >
            {params.row.leave_id}
          </Link>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Employee',
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const fullName = `${params.row.name || ''} ${
          params.row.last_name || ''
        }`.trim();
        return <span className="text-black-default">{fullName || '--'}</span>;
      },
    },
    {
      field: 'client',
      headerName: 'Client',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <span className="text-black-default">{params.value || '--'}</span>
      ),
    },
    {
      field: 'site_name',
      headerName: 'Site',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <span className="text-black-default">{params.value || '--'}</span>
      ),
    },
    {
      field: 'leave_type_name',
      headerName: 'Leave Type',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <span className="text-black-default">{params.value || '--'}</span>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1.5,
      sortable: true,
      renderCell: (params) => {
        return (
          <>
            <BadgeStatus status={params.value || ''} />
          </>
        );
      },
    },
    {
      field: 'leave_from',
      headerName: 'Leave From',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <span className="text-black-default">{params.value || '--'}</span>
      ),
    },
    {
      field: 'leave_to',
      headerName: 'Leave To',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <span className="text-black-default">{params.value || '--'}</span>
      ),
    },
    {
      field: 'no_of_days',
      headerName: 'Total Days',
      flex: 0.7,
      sortable: true,
      renderCell: (params) => (
        <span className="text-black-default">{params.value || '--'}</span>
      ),
    },
    {
      field: 'leave_coverage',
      headerName: 'Day Type',
      flex: 0.7,
      sortable: true,
      renderCell: (params) => {
        const coverageMap = {
          0: 'Full Day',
          1: 'Half Day (AM)',
          2: 'Half Day (PM)',
        };

        const value = coverageMap[params.value] || '--';

        return <span className="text-black-default">{value}</span>;
      },
    },
    {
      field: 'attachments',
      headerName: 'Attachments',
      flex: 0.8,
      sortable: false,
      renderCell: (params) => {
        const attachments = params.row.documents || [];
        if (attachments.length === 0)
          return <span className="text-black-default">--</span>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {attachments.map((file, idx) => (
              <div
                key={idx}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <a
                  href={`${SERVER_URL}/storage/leaves/${file.new_file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-flex align-items-center"
                >
                  <img
                    src={docImg}
                    alt="Document Icon"
                    style={{ width: '32px', height: '32px' }}
                  />
                </a>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Date Modified',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <span className="text-black-default">{params.value || '--'}</span>
      ),
    },
  ];

  const handleSelectionChange = (newSelection) => {
    setSelectionModel(newSelection);
  };

  const fetchLeaves = async (pagination, field, sort, search) => {
    try {
      setLoading(true);
      const fetchUrl = '/leaves/approval/list';
      const response = await newFetch(POST, fetchUrl, {
        field,
        sort,
        page: pagination.page,
        pageSize: pagination.pageSize,
        client_id: clientID,
        site_id: siteID,
        site_ids: siteIDs,
        type,
        search: search || '', // 🔹 Add search term
      });
      if (response.success) {
        setLeaves(response.data.list);
        setTotalRows(response.data.total_record);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const handleSortModelChange = (newSortModel) => setSortModel(newSortModel);

  const fetchTypes = async () => {
    try {
      let response = await newFetch(GET, `/claimtypes`);
      if (response.success) setClaimTypes(response.data);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchClients = async () => {
    let response = await newFetch(GET, `/client/${clientID}`);
    if (response.success) setClients(response.data);
  };

  const searchHandler = (e) => {
    setSearchText(e.target.value);
    // Reset to first page when searching
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleLeaveApproval = async (action, remarks = null) => {
    if (!selectionModel || selectionModel.length === 0) {
      return ToastUtil.error('Please select at least one leave request');
    }

    try {
      const response = await newFetch(POST, '/leaves/bulk-approval', {
        action,
        leave_ids: selectionModel,
        remarks,
      });

      if (response.success) {
        // Fix grammar for past tense
        const pastTense = action === 'approve' ? 'approved' : 'rejected';
        const leaveText = selectionModel.length === 1 ? 'leave' : 'leaves';

        ToastUtil.success(
          `Successfully ${pastTense} ${selectionModel.length} ${leaveText}`
        );

        fetchLeaves(
          paginationModel,
          sortModel[0]?.field,
          sortModel[0]?.sort,
          searchText
        );
        setSelectionModel([]);
        setRemarksText('');
        setOpenRejectModal(false);
      } else {
        ToastUtil.error(response.message || `${action} failed`);
      }
    } catch (err) {
      ToastUtil.error('Network error during approval');
      console.error(`${action} exception:`, err);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  useEffect(() => {
    const { field, sort } = sortModel[0] || {};
    fetchLeaves(paginationModel, field, sort);
    fetchClients();
  }, [sortModel, paginationModel, clientID, siteID, siteIDsKey]);

  useEffect(() => {
    setData(leaves || []);
    setLoading(false);
  }, [leaves]);

  useEffect(() => {
    const { field, sort } = sortModel[0] || {};
    const delayDebounce = setTimeout(() => {
      fetchLeaves(
        paginationModel,
        field,
        sort,
        searchText // Only send non-empty search terms
      );
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchText, paginationModel, sortModel]);

  return (
    <>
      <div
        className="container-fluid"
        style={{
          paddingLeft: '2rem',
          paddingRight: '2rem',
          paddingBottom: '6rem',
        }}
      >
        {clients.enable_leave_credits ? (
          <>
            <div className="border-bottom pb-2 py-2 mb-2 d-flex align-items-center">
              <h6 className="flex-grow-1 pt-4" style={{ color: '#A6478E' }}>
                Leave | For Approval
              </h6>
            </div>
            <Searchbar
              search={searchHandler}
              placeholder="Search"
              searchValue={searchText}
            />{' '}
            <div className="d-flex gap-3 mb-2">
              <button
                onClick={() => handleLeaveApproval('approve')}
                className="btn btn-brand adjust-fs adjust-shadow"
                style={{ width: '12rem' }}
                disabled={!selectionModel || selectionModel.length === 0}
              >
                Approve
              </button>
              <button
                onClick={() => setOpenRejectModal(true)}
                className="btn btn-danger adjust-fs adjust-shadow"
                style={{ width: '12rem' }}
                disabled={!selectionModel || selectionModel.length === 0}
              >
                Reject
              </button>
            </div>
            <div className="table-responsive rounded shadow bg-white">
              <Box sx={{ width: '100%', height: 1000 }}>
                {data && (
                  <DataGrid
                    columns={columns}
                    rows={data || []}
                    getRowId={(row) => row.leave_id}
                    rowCount={totalRows || 1}
                    loading={loading}
                    pageSizeOptions={[10, 20, 30, 40, 50, 80, 100]}
                    paginationModel={paginationModel}
                    paginationMode="server"
                    onPaginationModelChange={setPaginationModel}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 10 },
                      },
                    }}
                    style={{ minHeight: 100, fontSize: 14 }}
                    localeText={{ noRowsLabel: '' }}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    getRowHeight={() => 'auto'}
                    sx={{
                      [`& .${gridClasses.cell}`]: { py: 3 },
                      [`& .${gridClasses.columnHeader}`]: {
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#6c757d!important',
                      },
                      [`& .${gridClasses.row}`]: {
                        fontSize: '14px',
                        color: '#A6478E!important',
                        fontWeight: 700,
                      },
                    }}
                    checkboxSelection
                    onRowSelectionModelChange={handleSelectionChange}
                    rowSelectionModel={selectionModel}
                    disableRowSelectionOnClick={true}
                  />
                )}
              </Box>
            </div>
          </>
        ) : (
          <div className="text-muted pt-4">
            The client does not have access to this module.
          </div>
        )}
      </div>

      {/* ===== Reject Modal ===== */}
      {openRejectModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content p-3">
              <form
                onSubmit={(e) => {
                  e.preventDefault();

                  handleLeaveApproval('reject', remarksText);
                }}
                className="d-flex flex-column"
              >
                <h5>Remarks</h5>
                <textarea
                  className="form-control my-3"
                  rows={4}
                  value={remarksText}
                  onChange={(e) => setRemarksText(e.target.value)}
                  placeholder="Enter remarks..."
                />
                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setOpenRejectModal(false);
                      setRemarksText('');
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-danger">
                    Reject
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default withRouter(LeavesApproval);
